// ============================================================================
// XP et maîtrise d'une SKILLCARD — MIROIR de la RPC SQL
// `credit_card_xp_for_attempt` (migration 0278, appelée par `finish_attempt`).
// Garder synchro.
// ============================================================================
// Barème (0278 + 0280) : une BONNE réponse à une question reliée à la carte
// verse `question_xp` × la difficulté de LA QUESTION (facile ×1, moyenne ×1,5,
// difficile ×2) — `question_xp` est réglé par carte (défaut 10 depuis la
// migration 0508, figé à 1/100 de l'ancien XP max pour les cartes existantes).
// À chaque bonne réponse, jusqu'au plafond `xp_max` — qui est CALCULÉ (0508) :
// la somme de ce que rapportent toutes les questions reliées des exercices
// publiés (`card_hora_max_value` ⇄ `cardHoraMax`). La
// vitesse et les indices n'interviennent pas (ils comptent dans l'XP élève de
// `finish_attempt`, pas dans celle de la carte).
// Maîtrise = % de bonnes réponses sur les 50 dernières questions reliées
// (10 jusqu'à la migration 0340).
// ⚠️ Depuis la migration 0315 (HORA vivant), ce barème est la BASE : le gain
// réel est modulé par la maîtrise d'avant et le frein « exercice refait », et
// l'HORA s'érode dans le temps — cf. `hora-decay.ts`.
// ============================================================================

export const CARD_MASTERY_WINDOW = 50;
export const CARD_QUESTION_XP_MIN = 1;
export const CARD_QUESTION_XP_MAX = 10_000;
/** HORA par bonne réponse d'une nouvelle carte (défaut SQL de `cards.question_xp`, 0508). */
export const CARD_QUESTION_XP_DEFAULT = 10;

/**
 * HORA max d'une carte (migration 0508) — MIROIR de `card_hora_max_value` :
 * `question_xp` × somme des multiplicateurs de difficulté des questions
 * reliées des exercices publiés, arrondi, au moins 1.
 */
export function cardHoraMax(
  questionXp: number | null | undefined,
  difficulties: ReadonlyArray<string | null | undefined>,
): number {
  const weight = difficulties.reduce((sum, d) => sum + difficultyMultiplier(d), 0);
  return Math.max(1, Math.round((questionXp ?? CARD_QUESTION_XP_DEFAULT) * weight));
}

/** XP par bonne réponse par défaut : 1/100 de l'XP max, au moins 1. */
export function defaultCardQuestionXp(xpMax: number): number {
  return Math.max(1, Math.round(xpMax / 100));
}

/** XP effective par bonne réponse (réglage de la carte, sinon le défaut). */
export function effectiveCardQuestionXp(
  questionXp: number | null | undefined,
  xpMax: number,
): number {
  return questionXp ?? defaultCardQuestionXp(xpMax);
}

/** Multiplicateur de difficulté d'une question (miroir SQL 0280 ; inconnue = ×1). */
export function difficultyMultiplier(difficulty: string | null | undefined): number {
  switch ((difficulty ?? '').toLowerCase()) {
    case 'medium':
      return 1.5;
    case 'hard':
      return 2;
    default:
      return 1;
  }
}

/**
 * Pastille de difficulté d'un exercice relié à une carte (liste « Gagne des
 * HORA avec ») : Niveau 1 = facile ×1, Niveau 2 = moyen ×1,5, Niveau 3 =
 * difficile ×2 (Warmups 0295). Difficulté inconnue → null (pas de pastille).
 */
export function exerciseDifficultyBadge(
  difficulty: string | null | undefined,
): { level: 1 | 2 | 3; label: string; multiplier: string } | null {
  switch ((difficulty ?? '').toLowerCase()) {
    case 'easy':
      return { level: 1, label: 'Niveau 1', multiplier: '×1' };
    case 'medium':
      return { level: 2, label: 'Niveau 2', multiplier: '×1,5' };
    case 'hard':
      return { level: 3, label: 'Niveau 3', multiplier: '×2' };
    default:
      return null;
  }
}

/** XP d'une bonne réponse pondérée (arrondi comme `round()` SQL, au moins 1). */
export function weightedQuestionXp(questionXp: number, multiplier: number): number {
  return Math.max(1, Math.round(questionXp * multiplier));
}

export interface CardQuestionXpInput {
  correct: boolean;
  /** Difficulté de la question (easy / medium / hard). */
  difficulty?: string | null;
  /** Réglage de la carte (`cards.question_xp`), null = défaut. */
  questionXp: number | null | undefined;
  xpMax: number;
}

export function cardQuestionXp(input: CardQuestionXpInput): number {
  if (!input.correct) return 0;
  return weightedQuestionXp(
    effectiveCardQuestionXp(input.questionXp, input.xpMax),
    difficultyMultiplier(input.difficulty),
  );
}

/** Nouvelle XP de la carte, plafonnée. */
export function addCardXp(current: number, gain: number, xpMax: number): number {
  return Math.min(xpMax, current + Math.max(0, gain));
}

/**
 * XP encore à gagner avec un exercice : XP par bonne réponse × somme des
 * multiplicateurs de difficulté de ses questions reliées (`linkedWeight`, à
 * défaut leur nombre), sans dépasser ce qui manque pour atteindre l'XP max.
 */
export function exerciseXpPotential(input: {
  linkedQuestions: number;
  /** Somme des multiplicateurs de difficulté des questions reliées (0280). */
  linkedWeight?: number | null;
  questionXp: number;
  xpMax: number;
  currentXp?: number;
}): number {
  const remaining = Math.max(0, input.xpMax - (input.currentXp ?? 0));
  const weight = input.linkedWeight ?? Math.max(0, input.linkedQuestions);
  return Math.min(remaining, Math.round(weight * input.questionXp));
}

/** Maîtrise (0..100) à partir des résultats, du plus RÉCENT au plus ancien. */
export function cardMastery(recentOutcomes: readonly boolean[]): number {
  const window = recentOutcomes.slice(0, CARD_MASTERY_WINDOW);
  if (window.length === 0) return 0;
  return Math.round((100 * window.filter(Boolean).length) / window.length);
}
